import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material';

/**
 * Advanced iOS-style button styles with glassmorphism and neumorphism effects
 * These styles can be used across all pages for consistent, modern button design
 */

export const getIOSButtonStyles = (theme: Theme): SxProps => {
  const isDark = theme.palette.mode === 'dark';
  const bgColor = isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.5)';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.35)';
  
  return {
  backgroundColor: bgColor,
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: `1px solid ${borderColor}`,
  boxShadow: `
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -1px 0 rgba(0, 0, 0, 0.05)
  `,
  color: theme.palette.secondary.main,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover': {
    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.65)',
    backdropFilter: 'blur(25px) saturate(180%)',
    WebkitBackdropFilter: 'blur(25px) saturate(180%)',
    border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(255, 255, 255, 0.45)',
    boxShadow: `
      0 12px 40px rgba(0, 0, 0, 0.12),
      0 4px 12px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -1px 0 rgba(0, 0, 0, 0.08)
    `,
    transform: 'translateY(-2px)',
    '&::before': {
      left: '100%',
    },
  },
  '&:active': {
    transform: 'translateY(0px) scale(0.98)',
    boxShadow: `
      0 4px 16px rgba(0, 0, 0, 0.1),
      inset 0 2px 4px rgba(0, 0, 0, 0.1),
      inset 0 -2px 4px rgba(255, 255, 255, 0.1)
    `,
  },
  '&:disabled': {
    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.5)',
    color: theme.palette.text.disabled,
    backdropFilter: 'blur(10px)',
  },
  };
};

export const getIOSButtonPrimaryStyles = (theme: Theme): SxProps => {
  const rgb = {
    r: parseInt(theme.palette.primary.main.slice(1, 3), 16),
    g: parseInt(theme.palette.primary.main.slice(3, 5), 16),
    b: parseInt(theme.palette.primary.main.slice(5, 7), 16),
  };

  return {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    boxShadow: `
      0 8px 32px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25),
      0 2px 8px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1)
    `,
    color: 'white',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '16px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
      transition: 'left 0.5s',
    },
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
      backdropFilter: 'blur(25px) saturate(180%)',
      WebkitBackdropFilter: 'blur(25px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.35)',
      boxShadow: `
        0 12px 40px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35),
        0 4px 12px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25),
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -1px 0 rgba(0, 0, 0, 0.15)
      `,
      transform: 'translateY(-2px)',
      color: 'white',
      '&::before': {
        left: '100%',
      },
    },
    '&:active': {
      transform: 'translateY(0px) scale(0.98)',
      boxShadow: `
        0 4px 16px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3),
        inset 0 2px 4px rgba(0, 0, 0, 0.2),
        inset 0 -2px 4px rgba(255, 255, 255, 0.1)
      `,
    },
    '&:disabled': {
      backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`,
      color: 'rgba(255, 255, 255, 0.6)',
    },
  };
};

export const getIOSButtonSecondaryStyles = (theme: Theme): SxProps => {
  const rgb = {
    r: parseInt(theme.palette.secondary.main.slice(1, 3), 16),
    g: parseInt(theme.palette.secondary.main.slice(3, 5), 16),
    b: parseInt(theme.palette.secondary.main.slice(5, 7), 16),
  };

  return {
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`,
    boxShadow: `
      0 8px 32px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15),
      0 2px 8px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.4)
    `,
    color: theme.palette.secondary.main,
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '16px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: `linear-gradient(90deg, transparent, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2), transparent)`,
      transition: 'left 0.5s',
    },
    '&:hover': {
      backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
      boxShadow: `
        0 12px 40px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2),
        0 4px 12px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.5)
      `,
      transform: 'translateY(-2px)',
      '&::before': {
        left: '100%',
      },
    },
    '&:active': {
      transform: 'translateY(0px) scale(0.98)',
      boxShadow: `
        0 4px 16px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2),
        inset 0 2px 4px rgba(0, 0, 0, 0.1)
      `,
    },
  };
};

export const getIOSButtonOutlinedStyles = (theme: Theme): SxProps => ({
  backgroundColor: 'transparent',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: `
    0 4px 16px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.3)
  `,
  color: theme.palette.primary.main,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '0%',
    height: '100%',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}10)`,
    transition: 'width 0.3s',
  },
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}08`,
    borderColor: theme.palette.primary.dark,
    boxShadow: `
      0 8px 24px rgba(0, 0, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.4)
    `,
    transform: 'translateY(-2px)',
    '&::before': {
      width: '100%',
    },
  },
  '&:active': {
    transform: 'translateY(0px) scale(0.98)',
    boxShadow: `
      0 2px 8px rgba(0, 0, 0, 0.1),
      inset 0 2px 4px rgba(0, 0, 0, 0.05)
    `,
  },
});

